import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion, useScroll, useSpring } from "framer-motion";
import Reveal from "@/components/Reveal";
import {
  StaggerWords,
  StaggerList,
  StaggerItem,
  DrawLine,
  useActiveSection,
} from "@/components/Motion";

const VERTICALS = [
  {
    num: "I",
    title: "Mobility Access & Transition",
    belief:
      "The future of mobility cannot remain accessible only to the privileged.",
    body: "The transition to clean mobility must remain economically inclusive.",
    overview:
      "The mobility revolution is happening at two speeds — a fast, capital-rich premium frontier and a slower, fragile mass-mobility base. Folks Foundation operates on the latter. We work on retrofit ecosystems, secondary-market clean transport, decentralised charging access, and skilling pipelines that allow industrial labour to migrate cleanly into the next cycle. Mobility, in our thesis, cannot be a luxury good.",
    focus: [
      {
        t: "Retrofit Ecosystems",
        d: "Converting in-service internal-combustion fleets into compliant clean-mobility platforms. We engage OEMs, second-life battery operators and certification bodies to make retrofit a regulated industrial category rather than a fringe workaround.",
      },
      {
        t: "xEV Infrastructure",
        d: "Charging, swapping and depot-management infrastructure designed for commercial fleets, two-wheelers and three-wheelers — the vehicles that move the working economy — at competitive total cost of ownership.",
      },
      {
        t: "Public-Private Mobility Models",
        d: "Institutional structures that allow state transport corporations, fleet operators and municipalities to co-invest in clean mobility without exposing public balance sheets to private-cycle risk.",
      },
      {
        t: "EV Skilling Systems",
        d: "Vocational pipelines that re-credential auto-sector technicians, service workers and component engineers for the powertrain transition — the labour bridge between two industrial eras.",
      },
      {
        t: "Decentralised Charging Access",
        d: "Last-mile charging in tier-2 and tier-3 geographies, structured as civic infrastructure rather than as private convenience.",
      },
      {
        t: "Affordable Clean Transportation Frameworks",
        d: "Policy and finance scaffolding that compresses TCO for commercial users — operating-lease models, blended capital, residual-value underwriting.",
      },
    ],
    sectors: [
      "State transport corporations",
      "Tier-2 OEM suppliers",
      "Commercial fleet operators",
      "Municipal corporations",
      "Development finance institutions",
    ],
  },
  {
    num: "II",
    title: "Ecological Infrastructure",
    belief:
      "Ecology will become one of the most valuable infrastructures of the next century.",
    body: "The future economy will depend as much on ecological resilience as industrial capacity.",
    overview:
      "The next century's most valuable infrastructure will not be roads, ports or pipelines — it will be living systems. Biodiversity corridors, watersheds, soil-carbon banks, mineral recovery loops. These are the substrates on which industrial civilisation will stand. Folks Foundation treats ecology as critical national infrastructure and underwrites it on the same time-horizon and seriousness as a port or a grid.",
    focus: [
      {
        t: "Biodiversity Restoration",
        d: "Large-scale habitat reweaving across degraded landscapes — partnering with forestry departments, indigenous land stewards and ecological design firms to make restoration a measurable, contractable category.",
      },
      {
        t: "Water Resilience",
        d: "Aquifer recharge, watershed management, urban water reuse, and water-positive industrial design — water as a sovereign asset, not a free input.",
      },
      {
        t: "Circular Manufacturing",
        d: "Closed-loop industrial parks where the by-products of one process feed the inputs of another, designed at the master-plan stage rather than retrofitted.",
      },
      {
        t: "Battery Recovery Systems",
        d: "Reverse-logistics infrastructure for lithium-ion and emerging chemistries — turning the coming end-of-life wave into a strategic mineral reserve.",
      },
      {
        t: "Carbon Ecosystems",
        d: "Durable, verifiable carbon removal — soil, biochar, mineral, mangrove — paired with industrial offtake commitments at institutional scale.",
      },
      {
        t: "Regenerative Industrial Parks",
        d: "Master-planned manufacturing zones whose existence net-adds biodiversity and watershed health to the surrounding region.",
      },
      {
        t: "Climate-Adaptive Urban Infrastructure",
        d: "Heat-resilient streets, flood-tolerant drainage, urban canopy programs — adaptation infrastructure as a first-class civic asset.",
      },
    ],
    sectors: [
      "Ministries of environment & urban affairs",
      "Multilateral climate funds",
      "Circular-economy operators",
      "Re-insurance & nature-risk markets",
      "Indigenous and community land stewards",
    ],
  },
  {
    num: "III",
    title: "Human Capital & Transition",
    belief: "A successful transition is one society can absorb.",
    body: "Industrial transitions create instability before they create prosperity.",
    overview:
      "Every industrial transition has produced both prosperity and dispossession. The decarbonisation transition will be no different unless societies engineer absorption capacity in advance. Folks Foundation's human-capital work is about that absorption — keeping displaced labour whole, opening green-collar pathways, and giving young entrants meaningful entry into the regenerative economy on terms they would choose.",
    focus: [
      {
        t: "Workforce Reskilling",
        d: "Re-credentialing workers from coal, internal-combustion and legacy heavy industries into green-economy roles, with employer-anchored pipelines rather than abstract certificate factories.",
      },
      {
        t: "Semiconductor & EV Training Systems",
        d: "Specialist vocational pipelines for the components of the new economy — the technicians, operators and process engineers the next decade will need at industrial scale.",
      },
      {
        t: "AI-Industrial Education",
        d: "Preparing the workforce for the convergence of artificial intelligence and physical industrial systems — neither pure software nor pure shop-floor, but the seam where both meet.",
      },
      {
        t: "Youth Entrepreneurship Platforms",
        d: "Founder pathways for young innovators building inside the regenerative economy — mobility, climate hardware, circular manufacturing, agri-resilience.",
      },
      {
        t: "Green Manufacturing Employment",
        d: "Anchoring employment ecosystems around regenerative production hubs, with the housing, transport and care infrastructure that make those jobs livable.",
      },
    ],
    sectors: [
      "Skill development ministries",
      "ITIs, polytechnics & vocational networks",
      "Industrial-cluster employers",
      "Multilateral labour programs",
      "Youth & founder collectives",
    ],
  },
  {
    num: "IV",
    title: "Policy & Sovereign Collaboration",
    belief:
      "The next industrial cycle will be built through state-private alignment.",
    body: "Future infrastructure cannot scale without institutional coordination.",
    overview:
      "The next industrial cycle will not be built by markets alone. The scale and time-horizon of climate-grade infrastructure require state alignment, and the depth of capital required will be impossible without it. Folks Foundation operates as an institutional peer to ministries, sovereign funds, multilateral DFIs and cities — helping translate regenerative ambition into governance, contracts and capital structures that survive contact with the real economy.",
    focus: [
      {
        t: "Retrofit Policy Integration",
        d: "Embedding retrofit pathways into national clean-mobility roadmaps so they become a recognised, financeable, regulated category — not an informal repair sector.",
      },
      {
        t: "PPP Climate Infrastructure",
        d: "Structuring concession agreements for charging networks, circular industrial parks, watershed and adaptation projects — designed for long horizons and political continuity.",
      },
      {
        t: "Charging Ecosystem Policy",
        d: "Tariff design, land allocation frameworks, interoperability standards and grid-integration protocols for nationwide charging deployment.",
      },
      {
        t: "xEV Hub Development Frameworks",
        d: "Master-planning for xEV manufacturing and assembly hubs — industrial policy that compounds rather than dilutes ecological commitments.",
      },
      {
        t: "ESG Governance",
        d: "Building institutional ESG frameworks worth their name — material disclosure, real-economy accountability, and continuity with national climate commitments.",
      },
      {
        t: "Sustainable Urban Planning Systems",
        d: "Climate-adaptive zoning, urban canopy mandates, stormwater overlays and density frameworks engineered for the next-century city.",
      },
    ],
    sectors: [
      "Central & state ministries",
      "Sovereign and quasi-sovereign funds",
      "Multilateral DFIs",
      "Cities, municipalities & development authorities",
      "Sectoral regulators",
    ],
  },
  {
    num: "V",
    title: "Cultural & Civilizational Continuity",
    belief: "Civilizations survive when progress preserves memory.",
    body: "Modernization without continuity creates fragile societies.",
    overview:
      "Industrial modernisation that erases memory produces brittle societies. The places that have weathered every industrial revolution are the ones that carried their culture through it. Folks Foundation invests in the cultural infrastructure of the next century — heritage, indigenous knowledge, civic art, and human-centred urban form — as load-bearing infrastructure, not as decoration on the periphery of the real economy.",
    focus: [
      {
        t: "Heritage Ecosystems",
        d: "Treating monuments, neighbourhoods and craft economies as living systems rather than museum exhibits — restoration paired with continued use, continued livelihood, continued identity.",
      },
      {
        t: "Indigenous Knowledge Systems",
        d: "Integrating traditional ecological and social knowledge into modern governance — water, fire, forest and seed practice carried forward as part of the regenerative operating system.",
      },
      {
        t: "Public Art & Civic Identity",
        d: "Supporting public art and design as civic infrastructure — the visible language a community uses to recognise itself across generations of change.",
      },
      {
        t: "Ethical Technology Narratives",
        d: "Authoring the story of technological transition with humanism rather than techno-utopianism — narrative work as a serious institutional discipline.",
      },
      {
        t: "Human-Centered Urban Futures",
        d: "Designing cities around people, memory and meaning — not around throughput, optics or speculative valuation.",
      },
    ],
    sectors: [
      "Ministries of culture & heritage",
      "Heritage trusts & conservation bodies",
      "Urban-design studios",
      "Indigenous & traditional knowledge communities",
      "Civic & private cultural foundations",
    ],
  },
];

const easing = [0.16, 1, 0.3, 1];

export default function Vision() {
  const activeId = useActiveSection(VERTICALS.map((v) => `vertical-${v.num}`));
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  return (
    <div data-testid="vision-page" style={{ paddingTop: 168 }}>
      {/* Top scroll progress bar */}
      <motion.div
        aria-hidden
        style={{
          position: "fixed",
          top: 168,
          left: 0,
          right: 0,
          height: 2,
          background: "var(--mulberry)",
          transformOrigin: "left",
          scaleX: progress,
          zIndex: 60,
        }}
      />

      {/* INTRO */}
      <section
        className="container-wide"
        style={{ paddingTop: "8rem", paddingBottom: "6rem" }}
      >
        <Reveal>
          <p className="label-mono">— Vision / Thesis</p>
        </Reveal>
        <h1
          className="font-display font-bold mt-6"
          style={{
            fontSize: "clamp(2.5rem, 8vw, 7rem)",
            letterSpacing: "-0.045em",
            lineHeight: 0.92,
          }}
        >
          <StaggerWords text="One belief." baseDelay={0.05} />
          <br />
          <StaggerWords
            text="Five regenerative capabilities."
            baseDelay={0.45}
            highlight={["Five", "regenerative", "capabilities."]}
            stagger={0.07}
          />
        </h1>
      </section>

      {/* ONE BELIEF QUOTE */}
      <section
        data-testid="vision-belief"
        style={{
          background: "var(--charcoal-surface)",
          color: "var(--pearl)",
          padding: "8rem 0",
          borderTop: "1px solid var(--soft-border)",
          borderBottom: "1px solid var(--soft-border)",
        }}
      >
        <div className="container-wide">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-3">
              <p className="label-mono-light">— One Belief / 01</p>
              <DrawLine
                color="var(--mulberry-bright)"
                className="mt-4"
                delay={0.2}
              />
            </div>
            <div className="md:col-span-9">
              <p
                className="font-body italic"
                style={{
                  fontSize: "clamp(1.75rem, 4vw, 3.5rem)",
                  lineHeight: 1.15,
                }}
              >
                <StaggerWords
                  text="“The future cannot survive on economic growth alone."
                  stagger={0.045}
                  yOffset={20}
                  duration={0.85}
                />
                <br />
                <StaggerWords
                  text="It must compound ecological and human value.”"
                  baseDelay={0.5}
                  stagger={0.06}
                  yOffset={20}
                  duration={0.95}
                  highlight={["ecological", "and", "human", "value.”"]}
                  highlightColor="var(--mulberry-bright)"
                />
              </p>
              <Reveal delay={0.6}>
                <p className="label-mono-light mt-8">
                  — Folks Foundation Manifesto
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES / VERTICALS DEEP DIVE */}
      <section
        data-testid="vision-pillars"
        className="container-wide"
        style={{ paddingTop: "8rem", paddingBottom: "8rem" }}
      >
        <div className="grid md:grid-cols-12 gap-10">
          {/* Sticky sidebar */}
          <aside className="md:col-span-4">
            <div className="md:sticky md:top-48">
              <p className="label-mono mb-6">— Capabilities</p>
              <h2
                className="font-display font-bold"
                style={{
                  fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                  letterSpacing: "-0.035em",
                  lineHeight: 1,
                }}
              >
                Five systems
                <br />
                the next civilization
                <br />
                <span style={{ color: "var(--mulberry)" }}>depends on.</span>
              </h2>
              <ol className="mt-10 space-y-3 font-mono text-xs tracking-[0.2em]">
                {VERTICALS.map((v) => {
                  const isActive = activeId === `vertical-${v.num}`;
                  return (
                    <li key={v.num} className="relative">
                      <a
                        href={`#vertical-${v.num}`}
                        data-testid={`vertical-anchor-${v.num}`}
                        className="uppercase inline-flex items-center gap-3 transition-colors duration-500"
                        style={{
                          color: isActive
                            ? "var(--mulberry)"
                            : "rgba(17,18,19,0.55)",
                        }}
                      >
                        <motion.span
                          aria-hidden
                          animate={{
                            scaleX: isActive ? 1 : 0.15,
                            backgroundColor: isActive
                              ? "var(--mulberry)"
                              : "rgba(17,18,19,0.3)",
                          }}
                          transition={{ duration: 0.6, ease: easing }}
                          style={{
                            display: "inline-block",
                            width: 28,
                            height: 1,
                            transformOrigin: "left",
                          }}
                        />
                        {v.num} · {v.title}
                      </a>
                    </li>
                  );
                })}
              </ol>
            </div>
          </aside>

          {/* Capability articles */}
          <div className="md:col-span-8">
            {VERTICALS.map((v) => (
              <article
                key={v.num}
                id={`vertical-${v.num}`}
                data-testid={`vertical-${v.num}`}
                className="py-20 md:py-28 border-t border-[color:var(--soft-border)] first:border-t-0"
              >
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ staggerChildren: 0.08, delayChildren: 0.05 }}
                >
                  {/* Header */}
                  <StaggerItem>
                    <div className="flex items-baseline gap-6 mb-6">
                      <span
                        className="font-display font-bold"
                        style={{
                          fontSize: "clamp(2rem, 3.5vw, 3rem)",
                          color: "var(--mulberry)",
                          letterSpacing: "-0.04em",
                          lineHeight: 1,
                        }}
                      >
                        {v.num}
                      </span>
                      <span className="label-mono">— Capability {v.num}</span>
                    </div>
                  </StaggerItem>

                  <StaggerItem>
                    <h3
                      className="font-display font-bold"
                      style={{
                        fontSize: "clamp(2rem, 4.5vw, 3.75rem)",
                        letterSpacing: "-0.04em",
                        lineHeight: 0.95,
                      }}
                    >
                      {v.title}
                    </h3>
                  </StaggerItem>

                  <StaggerItem>
                    <p className="font-body text-xl md:text-2xl leading-snug mt-6 max-w-2xl">
                      {v.body}
                    </p>
                  </StaggerItem>

                  {/* OVERVIEW */}
                  <StaggerItem>
                    <div className="mt-12 grid md:grid-cols-12 gap-6">
                      <p className="md:col-span-3 label-mono">— Overview</p>
                      <p className="md:col-span-9 font-body text-lg md:text-xl leading-relaxed">
                        {v.overview}
                      </p>
                    </div>
                  </StaggerItem>

                  {/* FOCUS AREAS — detailed */}
                  <StaggerItem>
                    <div className="mt-16">
                      <div className="flex items-baseline justify-between gap-4 mb-8">
                        <p className="label-mono">— Focus Areas</p>
                        <span className="font-mono text-[11px] tracking-[0.22em] opacity-50">
                          {String(v.focus.length).padStart(2, "0")} pillars
                        </span>
                      </div>
                      <StaggerList stagger={0.07} delayChildren={0.1}>
                        {v.focus.map((f, i) => (
                          <StaggerItem
                            key={i}
                            y={18}
                            duration={0.65}
                            className="grid md:grid-cols-12 gap-4 md:gap-8 py-6 border-t border-[color:var(--soft-border)] last:border-b group"
                          >
                            <span className="md:col-span-1 pillar-num self-baseline">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <div className="md:col-span-4">
                              <h4
                                className="font-display font-bold transition-colors duration-500 group-hover:text-[color:var(--mulberry)]"
                                style={{
                                  fontSize:
                                    "clamp(1.125rem, 1.8vw, 1.5rem)",
                                  letterSpacing: "-0.025em",
                                  lineHeight: 1.1,
                                }}
                              >
                                {f.t}
                              </h4>
                            </div>
                            <p className="md:col-span-7 font-body text-base md:text-lg leading-relaxed text-[color:var(--charcoal)]/85">
                              {f.d}
                            </p>
                          </StaggerItem>
                        ))}
                      </StaggerList>
                    </div>
                  </StaggerItem>

                  {/* SECTORS ENGAGED */}
                  <StaggerItem>
                    <div className="mt-16 grid md:grid-cols-12 gap-6">
                      <p className="md:col-span-3 label-mono">
                        — Sectors Engaged
                      </p>
                      <ul className="md:col-span-9 flex flex-wrap gap-x-3 gap-y-3">
                        {v.sectors.map((s, i) => (
                          <li
                            key={i}
                            className="font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2 border border-[color:var(--soft-border)]"
                            style={{
                              color: "var(--charcoal)",
                              background: "rgba(99,34,67,0.04)",
                            }}
                          >
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </StaggerItem>

                  {/* CORE BELIEF */}
                  <StaggerItem>
                    <blockquote
                      className="mt-16 pl-6 font-body italic"
                      style={{
                        borderLeft: "2px solid var(--mulberry)",
                        fontSize: "clamp(1.25rem, 2vw, 1.875rem)",
                        lineHeight: 1.3,
                        color: "var(--charcoal)",
                      }}
                    >
                      “{v.belief}”
                      <span className="block label-mono mt-4">
                        — Core belief / Capability {v.num}
                      </span>
                    </blockquote>
                  </StaggerItem>

                  {/* ENGAGE LINE */}
                  <StaggerItem>
                    <div className="mt-12 flex flex-wrap items-center gap-6">
                      <Link
                        to="/contact"
                        data-testid={`vertical-${v.num}-engage`}
                        className="btn-mulberry"
                      >
                        Engage on Capability {v.num} <ArrowUpRight size={14} />
                      </Link>
                      <span className="label-mono opacity-70">
                        — institutional & sovereign collaboration
                      </span>
                    </div>
                  </StaggerItem>
                </motion.div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="vision-cta"
        style={{
          background: "var(--charcoal)",
          color: "var(--pearl)",
          padding: "8rem 0",
        }}
      >
        <div className="container-wide grid md:grid-cols-12 gap-10 items-end">
          <h3
            className="md:col-span-8 font-display font-bold"
            style={{
              fontSize: "clamp(2rem, 5vw, 4rem)",
              letterSpacing: "-0.04em",
              lineHeight: 0.95,
            }}
          >
            <StaggerWords
              text="The next industrial cycle will be built through state-private alignment."
              stagger={0.05}
              highlight={["state-private", "alignment."]}
              highlightColor="var(--mulberry-bright)"
            />
          </h3>
          <Reveal delay={0.4} className="md:col-span-4 md:justify-self-end">
            <Link
              to="/future"
              data-testid="vision-cta-future"
              className="btn-ghost-light"
            >
              The long-duration bet <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
